import React, { useState, useEffect } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import NumericKeypad from '@/components/features/shared/NumericKeypad';
import OTPInput from '@/components/features/shared/OTPInput';
import LockerSelection from '@/components/features/deposit/LockerSelection';
import { verifyPhone, sendOTP, verifyOTP, getParcelDataByBarcode, getCompanies, createCompanyDeposit  } from '@/services';
import { useTranslation } from 'react-i18next';
import { Company } from '@/types/Company';
import axios from 'axios';
import LockerMonitor from '../shared/LockerMonitor';

const CompanyDeposit: React.FC = () => {
    const { t } = useTranslation();

    const {
        step,
        setStep,
        company,
        setCompany,
        phone,
        setPhone,
        otp,
        setOTP,
        barcode,
        setBarcode,
        error,
        setError,
        assignedLocker,
        setAssignedLocker,
        lockerSize,
        lockerType,
    } = useAppContext();

    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLockerClosed, setIsLockerClosed] = useState<boolean>(false);
    const [isProcessingBarcode, setIsProcessingBarcode] = useState<boolean>(false);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const fetchedCompanies = await getCompanies();
                setCompanies(fetchedCompanies);
            } catch (error) {
                handleError(error, 'fetchCompanies');
            }
        };

        fetchCompanies();
    }, [setError]);

    useEffect(() => {
        if (step === 5) {
            const handleKeyPress = async (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    if (barcode.length > 0) {
                        await processBarcode();
                    }
                } else if (/^\d$/.test(event.key) && barcode.length < BARCODE_LENGTH) {
                    const newBarcode = barcode + event.key;
                    setBarcode(newBarcode);
                }
            };

            window.addEventListener('keypress', handleKeyPress);
            return () => {
                window.removeEventListener('keypress', handleKeyPress);
            };
        }
    }, [step, barcode, setBarcode]);

    const handleError = (error: any, context: string) => {
        console.error(`Error in ${context}:`, error);
        
        if (axios.isAxiosError(error)) {
            // Handle API errors
            if (error.response) {
                // Server responded with error status
                switch (error.response.status) {
                    case 400:
                        setError(t('invalidRequestError'));
                        break;
                    case 404:
                        setError(t('barcodeNotFoundError'));
                        break;
                    case 401:
                        setError(t('authenticationError'));
                        break;
                    case 500:
                        setError(t('serverError'));
                        break;
                    default:
                        setError(t('unexpectedError'));
                }
            } else if (error.request) {
                // Request made but no response received
                setError(t('networkError'));
            } else {
                setError(t('requestError'));
            }
        } else {
            // Handle non-API errors
            setError(t('generalError'));
        }
    };

    const handleCompanySelect = (selectedCompany: string) => {
        setCompany(selectedCompany);
        setStep(3);
    };

    const handlePhoneEnter = async () => {
        try {
            const isValid = await verifyPhone(phone, company);
            if (isValid) {
                setError('');
                await sendOTP(phone);
                setStep(4);
            } else {
                setError(t('invalidPhoneError'));
            }
        } catch (error) {
            handleError(error, 'phoneVerification');
        }
    };

    const handleOTPEnter = async () => {
        try {
            const isValid = await verifyOTP(phone, otp);
            if (isValid) {
                setError('');
                setStep(5);
            } else {
                setError(t('invalidOtpError'));
            }
        } catch (error) {
            handleError(error, 'otpVerification');
        }
    };

    const BARCODE_LENGTH = 14;

    const processBarcode = async () => {
        if (isProcessingBarcode) return;
        
        setIsProcessingBarcode(true);
        try {
            setError('');
            const data = await getParcelDataByBarcode(barcode);
            if (data.recipientPhone) {
                setStep(6);
            } else {
                setError(t('barcodeNoRecipientError'));
            }
        } catch (error) {
            handleError(error, 'barcodeProcessing');
        } finally {
            setIsProcessingBarcode(false);
        }
    };

    const handleBarcodeKeypadInput = (key: string) => {
        if (barcode.length < BARCODE_LENGTH && /^\d+$/.test(key)) {
            const newBarcode = barcode + key;
            setBarcode(newBarcode);
        }
    };

    const handleStartNewScan = () => {
        setBarcode('');
        setError('');
    };

    const handleLockerSelectionComplete = async () => {
        // Clear any previous errors
        setError('');
    
        // Validate required fields
        if (!company || !phone || !barcode || !lockerSize || !lockerType) {
          setError(t('missingRequiredFields'));
          return;
        }
    
        try {
          const request = {
            companyId: company,
            recipientPhone: phone,
            trackingNumber: barcode,
            size: lockerSize,
            requiresCooling: lockerType === 'Refrigerated',
            requiresSecurity: lockerType === 'High-Security'
          };
    
          const response = await createCompanyDeposit(request);
    
          // Check if we have a valid locker ID
          if (!response.lockerId) {
            setError(t('noLockerAssigned'));
            return;
          }
    
          // Success path
          setAssignedLocker(response.lockerId);
          setStep(9);
    
        } catch (error) {
          // Handle specific error cases
          if (axios.isAxiosError(error)) {
            const statusCode = error.response?.status;
            const errorMessage = error.response?.data?.message;
    
            switch (statusCode) {
              case 400:
                // Handle validation errors
                setError(errorMessage || t('invalidRequestError'));
                break;
              case 404:
                // Handle not found errors (e.g., barcode not found)
                setError(t('barcodeNotFoundError'));
                break;
              case 409:
                // Handle conflicts (e.g., locker already assigned)
                setError(t('lockerAlreadyAssigned'));
                break;
              case 422:
                // Handle business rule violations
                setError(errorMessage || t('businessRuleViolation'));
                break;
              case 503:
                // Handle service unavailable (e.g., no lockers available)
                setError(t('noLockersAvailable'));
                break;
              default:
                // Handle unexpected errors
                setError(t(errorMessage) || t('unexpectedError'));
            }
    
            // Log error for debugging
            console.error('Company deposit error:', {
              status: statusCode,
              message: errorMessage
            });
          } else if (error instanceof Error) {
            // Handle non-Axios errors
            setError(t(error.message) || t('unexpectedError'));
            console.error('Non-Axios error:', error);
          } else {
            // Handle unknown errors
            setError(t('unexpectedError'));
            console.error('Unknown error:', error);
          }
        }
      };

    useEffect(() => {
        setIsLockerClosed(false);
    }, [step]);

    const handleLockerStateChange = (isClosed: boolean) => {
        console.log('Locker state changed:', isClosed); // Add logging
        setIsLockerClosed(isClosed);
    };

    const handleRestart = () => {
        setCompany('');
        setPhone('');
        setOTP('');
        setBarcode('');
        setError('');
        setStep(0);
        setIsLockerClosed(false); // Reset locker state
    };

    const renderStep = () => {
        switch (step) {
            case 2:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('companySelect')}</h2>
                        <div className="grid grid-cols-2 gap-2 mt-4">
                            {companies.map((comp) => (
                                <Button
                                    key={comp.id}
                                    onClick={() => handleCompanySelect(comp.id)}
                                    variant="outline"
                                    className="h-14 text-xl font-semibold"
                                >
                                    {comp.name}
                                </Button>
                            ))}
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('enterPhoneNumber')}</h2>
                        <h3 className='font-bold text-foreground'>{company}</h3>
                        <Input
                            type="tel"
                            className="text-foreground"
                            placeholder={t('enterYourPhoneNumber')}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        <NumericKeypad
                            onKeyPress={(key) => setPhone(phone + key)}
                            onClear={() => setPhone('')}
                            onBackspace={() => setPhone(phone.slice(0, -1))}
                        />
                        <Button onClick={handlePhoneEnter} variant="outline" size="lg">{t('confirm')}</Button>
                    </>
                );
            case 4:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('enterOTP')}</h2>
                        <OTPInput value={otp} onChange={setOTP} />
                        <NumericKeypad
                            onKeyPress={(key) => setOTP(otp + key)}
                            onClear={() => setOTP('')}
                            onBackspace={() => setOTP(otp.slice(0, -1))}
                        />
                        <Button onClick={handleOTPEnter} variant="outline" size="lg">{t('confirm')}</Button>
                        <Button onClick={() => setOTP('')} variant="outline" size="lg">{t('resendOTP')}</Button>
                    </>
                );
            case 5:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('scanBarcode')}</h2>
                        <div className="space-y-4">
                            <Input
                                type="text"
                                placeholder={t('scanBarcode')}
                                value={barcode}
                                readOnly
                                className="text-center text-xl text-foreground"
                            />
                            <NumericKeypad
                                onKeyPress={handleBarcodeKeypadInput}
                                onClear={() => setBarcode('')}
                                onBackspace={() => setBarcode(barcode.slice(0, -1))}
                            />
                            <div className="space-y-2">
                                <Button 
                                    onClick={processBarcode} 
                                    variant="default" 
                                    size="lg" 
                                    className="w-full"
                                    disabled={isProcessingBarcode}
                                >
                                    {t('submitBarcode')}
                                </Button>
                            </div>
                        </div>
                    </>
                );
            case 6:
                return (
                    <LockerSelection onComplete={handleLockerSelectionComplete} showHoldTimeInput={false} />
                );
            case 9:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">{t('makeDeposit')}</h2>
                        <p className="text-foreground">{t('assignedLocker')}</p>
                        <p className='text-3xl font-bold text-foreground'>{assignedLocker}</p>
                        <LockerMonitor 
                            lockerId={assignedLocker} 
                            onStateChange={handleLockerStateChange}
                        />
                        <Button 
                            onClick={() => setStep(5)} 
                            variant="outline" 
                            size="lg" 
                            className="w-full"
                            disabled={!isLockerClosed}
                        >
                            {t('anotherDeposit')}
                        </Button>
                        <Button 
                            onClick={handleRestart} 
                            variant="outline" 
                            size="lg" 
                            className="w-full"
                            disabled={!isLockerClosed}
                        >
                            {t('finish')}
                        </Button>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4">
            {renderStep()}
            <Button onClick={handleRestart} variant="outline" size="lg">{t('cancel')}</Button>
        </div>
    );
};

export default CompanyDeposit;